import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/button";
import { Icon } from "@/components/icons";

type Plan = "screening" | "monitoring" | "both";

const OPTIONS: { id: Plan; icon: string; title: string; description: string }[] = [
  {
    id: "screening",
    icon: "search",
    title: "Screening",
    description: "Single and batch checks against sanctions, PEP, and adverse media lists.",
  },
  {
    id: "monitoring",
    icon: "visibility",
    title: "Monitoring",
    description: "Continuous re-screening with alerts when a monitored name's risk changes.",
  },
  {
    id: "both",
    icon: "manage_search",
    title: "Screening + Monitoring",
    description: "Both tools in one plan, billed together.",
  },
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  // Figma frame 15146:8200 shows Monitoring preselected.
  const [selected, setSelected] = useState<Plan>("monitoring");

  function handleContinue() {
    navigate("/screening-portal");
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-grey-25 p-3">
      <div className="flex w-[560px] flex-col gap-8 rounded-[8px] border border-grey-200 bg-white p-6 shadow-soft">
        <div className="flex flex-col gap-1">
          <h1 className="text-h4 text-black">Set up your workspace</h1>
          <p className="text-small text-grey-900">
            Tell us how you&apos;ll use sanctions.io and we&apos;ll tailor the workspace to match.
          </p>
        </div>

        <div className="flex flex-col gap-1" role="radiogroup" aria-label="Plan">
          {OPTIONS.map((opt) => (
            <PlanCard
              key={opt.id}
              icon={opt.icon}
              title={opt.title}
              description={opt.description}
              selected={selected === opt.id}
              onSelect={() => setSelected(opt.id)}
            />
          ))}
        </div>

        <div className="flex items-stretch gap-1">
          <Button
            type="button"
            hierarchy="secondary"
            size="md"
            className="flex-1"
            onClick={handleContinue}
          >
            Skip for now
          </Button>
          <Button
            type="button"
            hierarchy="primary"
            size="md"
            className="flex-1"
            onClick={handleContinue}
            rightIcon={<Icon name="arrow_forward" size={20} />}
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
}

function PlanCard({
  icon,
  title,
  description,
  selected,
  onSelect,
}: {
  icon: string;
  title: string;
  description: string;
  selected: boolean;
  onSelect: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={[
        "flex items-start gap-3 rounded-[4px] border p-4 text-left shadow-flat transition-colors",
        selected ? "border-green-400 bg-green-100" : "border-grey-200 bg-white hover:bg-grey-50",
      ].join(" ")}
    >
      <span
        className={[
          "flex size-9 shrink-0 items-center justify-center rounded-[4px]",
          selected ? "bg-green-600 text-white" : "bg-grey-50 text-grey-900",
        ].join(" ")}
      >
        <Icon name={icon} size={20} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span
          className={["text-body font-semibold", selected ? "text-green-700" : "text-black"].join(
            " ",
          )}
        >
          {title}
        </span>
        <span className={["text-small", selected ? "text-green-700" : "text-grey-900"].join(" ")}>
          {description}
        </span>
      </span>
    </button>
  );
}
